/* Amor1970s · Navidad 2026 — behaviour for sections amor-xmas-*.
   Plain custom elements, no dependencies. Safe to load once per page;
   each element re-initialises when the Theme Editor reloads a section. */
(() => {
  if (window.__amorXmas) return;
  window.__amorXmas = true;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mod = (a, n) => ((a % n) + n) % n;

  /** Horizontal swipe that leaves vertical scrolling to the browser. */
  function swipe(el, { onMove, onEnd }) {
    let s = null, axis = null;
    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      s = { x: e.clientX, y: e.clientY, id: e.pointerId }; axis = null;
    });
    el.addEventListener('pointermove', (e) => {
      if (!s || s.id !== e.pointerId) return;
      const dx = e.clientX - s.x, dy = e.clientY - s.y;
      if (!axis) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') { el.setPointerCapture?.(e.pointerId); el.classList.add('is-dragging'); }
      }
      if (axis === 'x') onMove?.(dx);
    });
    const end = (e) => {
      if (!s || s.id !== e.pointerId) return;
      const dx = axis === 'x' ? e.clientX - s.x : 0;
      const wasDrag = axis === 'x';
      s = null; axis = null; el.classList.remove('is-dragging');
      onEnd(dx, wasDrag);
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('click', (e) => { if (el.__justDragged) { e.preventDefault(); e.stopPropagation(); } }, true);
  }

  /* ---------- Hero: pointer parallax on the baubles ---------- */
  class AmxHero extends HTMLElement {
    connectedCallback() {
      if (reduced() || !window.matchMedia('(hover: hover)').matches) return;
      let raf = 0;
      this._move = (e) => {
        const r = this.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * 2 - 1, py = ((e.clientY - r.top) / r.height) * 2 - 1;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => { this.style.setProperty('--px', px.toFixed(3)); this.style.setProperty('--py', py.toFixed(3)); });
      };
      this.addEventListener('pointermove', this._move);
    }
    disconnectedCallback() { if (this._move) this.removeEventListener('pointermove', this._move); }
  }

  /* ---------- Product rail: arrows + progress line ---------- */
  class AmxRail extends HTMLElement {
    connectedCallback() {
      const rail = this.querySelector('.amx-rail'), bar = this.querySelector('.amx-progress span');
      if (!rail) return;
      const update = () => {
        const max = rail.scrollWidth || 1;
        if (bar) { bar.style.left = (rail.scrollLeft / max) * 100 + '%'; bar.style.width = (rail.clientWidth / max) * 100 + '%'; }
      };
      rail.addEventListener('scroll', update, { passive: true });
      new ResizeObserver(update).observe(rail);
      this.querySelectorAll('[data-dir]').forEach((b) => b.addEventListener('click', () => {
        rail.scrollBy({ left: Number(b.dataset.dir) * rail.clientWidth * 0.9, behavior: reduced() ? 'auto' : 'smooth' });
      }));
      update();
    }
  }

  /* ---------- Gift finder: tabs ---------- */
  class AmxFinder extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((t, i) => t.addEventListener('click', () => this.select(i, false)));
      this.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
        const cur = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        const n = this.tabs.length;
        const map = { ArrowRight: cur + 1, ArrowLeft: cur - 1, Home: 0, End: n - 1 };
        if (!(e.key in map)) return;
        e.preventDefault();
        this.select(mod(map[e.key], n), true);
      });
    }
    select(i, focus) {
      this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === i)); t.tabIndex = k === i ? 0 : -1; });
      this.panels.forEach((p, k) => {
        p.hidden = k !== i;
        if (k === i) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); }
      });
      if (focus) this.tabs[i].focus();
    }
  }

  /* ---------- Poke cards: infinite carousel, no clone reset ----------
     `pos` is unbounded; slot k shows card (pos + k) mod N, keyed by its
     absolute index, so cards glide and never jump back to the first. */
  class AmxPoke extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.amx-poke__stage');
      this.tpl = [...this.querySelectorAll('template[data-card]')];
      this.count = this.querySelector('.amx-poke__count-n');
      this.N = this.tpl.length;
      if (!this.stage || !this.N) return;
      this.pos = 0; this.dx = 0; this.nodes = new Map();
      this.measure();
      new ResizeObserver(() => { this.measure(); this.render(true); }).observe(this.stage);
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
      });
      swipe(this.stage, {
        onMove: (dx) => { this.dx = dx; this.render(); },
        onEnd: (dx, wasDrag) => {
          this.stage.__justDragged = wasDrag && Math.abs(dx) > 6;
          setTimeout(() => { this.stage.__justDragged = false; }, 0);
          this.dx = 0;
          if (Math.abs(dx) >= 40) this.go(dx < 0 ? Math.max(1, Math.round(-dx / this.slot)) : -Math.max(1, Math.round(dx / this.slot)));
          else this.render();
        },
      });
      if (reduced()) this.stage.classList.add('is-instant');
      this.render(true);
    }
    measure() {
      const w = this.stage.clientWidth;
      this.slot = w >= 760 ? Math.min(330, w / 3.6) : w * 0.7;
      this.stage.style.height = this.slot * 0.88 + 24 + 'px';
    }
    go(step) { this.pos += step; this.render(); }
    render(instant) {
      if (instant) this.stage.classList.add('is-instant');
      const keep = new Set();
      for (let k = -3; k <= 3; k++) {
        const abs = this.pos + k; keep.add(abs);
        let el = this.nodes.get(abs);
        if (!el) {
          el = this.tpl[mod(abs, this.N)].content.firstElementChild.cloneNode(true);
          el.addEventListener('click', () => { const kk = abs - this.pos; if (kk) this.go(kk); });
          this.stage.appendChild(el); this.nodes.set(abs, el);
          el.style.transform = `translateX(calc(-50% + ${(k + Math.sign(k || 1)) * this.slot}px)) scale(.86)`;
          void el.offsetWidth;
        }
        const x = k * this.slot + this.dx, dist = Math.min(Math.abs(x) / this.slot, 2);
        el.style.width = this.slot * 0.88 + 'px';
        el.style.transform = `translateX(calc(-50% + ${x}px)) scale(${1 - Math.min(dist, 1) * 0.14})`;
        el.style.opacity = Math.abs(k) >= 3 ? 0 : 1 - Math.max(0, dist - 1) * 0.35;
        el.style.zIndex = 10 - Math.abs(k);
        el.classList.toggle('is-active', k === 0);
        el.setAttribute('aria-hidden', k === 0 ? 'false' : 'true');
        const img = el.querySelector('img'); if (img) img.alt = k === 0 ? img.dataset.alt || '' : '';
      }
      for (const [abs, el] of this.nodes) if (!keep.has(abs)) { el.remove(); this.nodes.delete(abs); }
      if (this.count) this.count.textContent = mod(this.pos, this.N) + 1;
      if (instant) requestAnimationFrame(() => { if (!reduced()) this.stage.classList.remove('is-instant'); });
    }
  }

  /* ---------- 3D frames: one stage, arrows / swipe / keys / thumbs ---------- */
  class AmxFrames extends HTMLElement {
    connectedCallback() {
      this.imgs = [...this.querySelectorAll('.amx-stage__img')];
      this.thumbs = [...this.querySelectorAll('.amx-thumb')];
      this.dots = [...this.querySelectorAll('.amx-dots span')];
      this.num = this.querySelector('.amx-frames__count');
      this.name = this.querySelector('.amx-frames__name');
      this.N = this.imgs.length; this.i = 0;
      if (!this.N) return;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1));
      this.thumbs.forEach((t, k) => t.addEventListener('click', () => this.show(k, k > this.i ? 1 : -1)));
      this.querySelector('.amx-stage')?.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
      });
      const view = this.querySelector('.amx-stage__view');
      if (view) swipe(view, { onEnd: (dx) => { if (dx <= -40) this.go(1); else if (dx >= 40) this.go(-1); } });
    }
    go(step) { this.show(mod(this.i + step, this.N), step); }
    show(i, dir) {
      this.i = i;
      this.imgs.forEach((im, k) => { im.dataset.dir = dir; im.classList.toggle('is-active', k === i); im.setAttribute('aria-hidden', String(k !== i)); });
      this.thumbs.forEach((t, k) => t.setAttribute('aria-pressed', String(k === i)));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      if (this.num) this.num.textContent = String(i + 1).padStart(2, '0');
      if (this.name) this.name.textContent = this.imgs[i].dataset.name || '';
    }
  }

  const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };
  define('amx-hero', AmxHero);
  define('amx-rail', AmxRail);
  define('amx-finder', AmxFinder);
  define('amx-poke', AmxPoke);
  define('amx-frames', AmxFrames);
})();
